/**
 * SiteContentContext — provides live content fetched from the backend at
 * /api/site/content. Falls back to the local mock module until the fetch
 * completes so the first paint is instantaneous. Consumers destructure the
 * fields they need via `useSite()`.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { fetchSiteContent } from './api';
import * as mock from '../mock/mock';

const DEFAULTS = {
  brand: mock.brand,
  CTA: mock.CTA,
  courses: mock.courses,
  courseIncludes: mock.courseIncludes,
  tutors: mock.tutors,
  faqs: mock.faqs,
  pillars: mock.pillars,
  howItWorks: mock.howItWorks,
  results: mock.results,
  trustpilot: mock.trustpilot,
  whatsappTestimonials: [],
  siteSettings: {
    bannerEnabled: true,
    bannerText: 'Ready to boost your grade? Book a free trial.',
    bannerSubtext: 'Speak to a qualified teacher. No obligation.',
    ctaPrimaryLabel: 'Book a free trial',
    ctaPrimaryUrl: 'https://sciencesorted.tutorbird.com/',
    ctaSecondaryLabel: 'View courses',
    ctaSecondaryUrl: '/courses',
    responseTime: 'We respond within 24 hours.',
    socials: {},
  },
};

const SiteContentContext = createContext(DEFAULTS);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    fetchSiteContent()
      .then((data) => {
        if (cancelled || !data) return;
        setContent((prev) => ({ ...prev, ...data }));
      })
      .catch(() => {
        // Silent - the UI already shows fallback content from mock.js.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => content, [content]);
  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSite() {
  return useContext(SiteContentContext);
}
